import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } };

const CATEGORIES = [
  {
    title: "Food & Beverage Operations",
    items: [
      { name: "Delivery-Only Brands", desc: "For Uber Eats / DoorDash operators.", highlight: true },
      { name: "Catering & Meal Prep Companies", desc: "Production without build-outs.", highlight: true },
      { name: "Food Truck Commissary Space", desc: "Prep and storage for mobile vendors.", highlight: true },
      { name: "Restaurant Expansion", desc: "Boost delivery radius without new retail leases.", highlight: true },
      { name: "Startup Food Concepts", desc: "Low-risk testing ground for new menus.", highlight: true },
    ],
  },
  {
    title: "Retail & CPG Production",
    items: [
      { name: "Small-Batch CPG Manufacturing", desc: "Sauces, spices, packaged goods.", highlight: false },
      { name: "Subscription Food Box Assembly", desc: "Controlled environment for packing.", highlight: false },
      { name: "Farmer's Market Vendor Prep", desc: "Weekly production space.", highlight: false },
    ],
  },
  {
    title: "Corporate & Enterprise Uses",
    items: [
      { name: "Corporate Campus Micro-Kitchens", desc: "On-site employee food service.", highlight: true },
      { name: "R&D Test Kitchens", desc: "Product development for food brands.", highlight: true },
      { name: "Training Kitchens", desc: "Operator or franchisee training.", highlight: true },
    ],
  },
  {
    title: "Municipal & Public-Sector Applications",
    items: [
      { name: "Emergency Response Kitchens", desc: "Disaster relief food distribution.", highlight: false },
      { name: "Community Feeding Programs", desc: "Nonprofit or city-run meal service.", highlight: false },
      { name: "School District Satellite Kitchens", desc: "Overflow capacity for cafeterias.", highlight: false },
    ],
  },
  {
    title: "Healthcare & Institutional Uses",
    items: [
      { name: "Hospital Satellite Kitchens", desc: "Specialty diet or overflow prep.", highlight: true },
      { name: "University Campus Kitchens", desc: "Dining expansion without major construction.", highlight: true },
    ],
  },
  {
    title: "Events & Hospitality",
    items: [
      { name: "Stadium/Arena Concessions", desc: "Rapid deployment for events.", highlight: false },
      { name: "Festival & Fairground Kitchens", desc: "Seasonal operations.", highlight: false },
    ],
  },
  {
    title: "Developer & Landowner Applications",
    items: [
      { name: "Parking Lot Activation", desc: "Add food operators to existing commercial sites.", highlight: true },
      { name: "Underutilized Parcel Monetization", desc: "Quick-start revenue for small lots.", highlight: true },
      { name: "Mixed-Use Development Support", desc: "Plug-in food service nodes.", highlight: false },
    ],
  },
];

export default function UsesPage() {
  return (
    <div>
      {/* Hero */}
      <section style={{ backgroundColor: "#2d2d2d" }} className="bp-grid">
        <div className="w-full max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24 pt-28 pb-14 md:py-20">
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xs uppercase tracking-[0.25em] font-bold mb-6"
            style={{ color: "#d4782f" }}
          >
            Applications
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.7 }}
            className="text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.08] mb-6"
            style={{ color: "#FFFFFF", fontFamily: "'Space Grotesk', sans-serif" }}
          >
            One Platform.<br />
            <span style={{ color: "#d4782f" }}>Infinite Applications.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="text-sm md:text-lg leading-relaxed max-w-2xl"
            style={{ color: "#A1A5B0" }}
          >
            Our 320 sq. ft. modular kitchen units serve far more than traditional restaurants.
            Explore the full range of commercial applications.
          </motion.p>
        </div>
      </section>

      {/* Uses Grid */}
      <section style={{ backgroundColor: "#FAFAFA" }}>
        <div className="w-full max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24 py-12 md:py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {CATEGORIES.map((cat, i) => (
              <motion.div
                key={cat.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: i * 0.08, duration: 0.5 }}
                whileHover={{ y: -6, boxShadow: "0 20px 50px rgba(212,120,47,0.1)", borderColor: "rgba(212,120,47,0.3)" }}
                className="p-7 md:p-8 transition-all"
                style={{
                  backgroundColor: "#FFFFFF",
                  border: "1px solid rgba(26,29,35,0.06)",
                  boxShadow: "0 2px 20px rgba(0,0,0,0.03)",
                }}
              >
                <h3
                  className="text-lg md:text-xl font-bold tracking-tight mb-6 pb-4"
                  style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    color: "#1A1D23",
                    borderBottom: "2px solid #d4782f",
                  }}
                >
                  {cat.title}
                </h3>
                <div className="space-y-4">
                  {cat.items.map((item, j) => (
                    <motion.div
                      key={j}
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.2 + j * 0.06 }}
                      className="flex items-start gap-3"
                    >
                      <div
                        className="w-2 h-2 rounded-full mt-2 flex-shrink-0"
                        style={{ backgroundColor: "#d4782f" }}
                      />
                      <div>
                        <p
                          className="text-sm font-bold leading-snug"
                          style={{ color: item.highlight ? "#d4782f" : "#1A1D23" }}
                        >
                          {item.name}
                        </p>
                        <p className="text-xs mt-0.5 leading-relaxed" style={{ color: "#4A4F5C" }}>
                          {item.desc}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ backgroundColor: "#2d2d2d" }}>
        <div className="w-full max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24 py-12 md:py-16 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-2xl md:text-4xl font-bold tracking-tight mb-6"
            style={{ fontFamily: "'Space Grotesk', sans-serif", color: "#FFFFFF" }}
          >
            Don't See Your Use Case?<br />
            <span style={{ color: "#d4782f" }}>Let's Talk.</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-sm md:text-base mb-8"
            style={{ color: "#A1A5B0" }}
          >
            Our modular platform adapts to virtually any commercial kitchen need.
          </motion.p>
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }} className="inline-block">
            <Link
              to="/contact"
              data-testid="uses-cta-contact-button"
              className="inline-flex items-center gap-2 px-8 py-4 font-bold tracking-wide text-sm glow-btn rounded-none"
              style={{ backgroundColor: "#d4782f", color: "#FFFFFF" }}
            >
              Contact Us <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
